import { Button, Col, Form, Row } from 'react-bootstrap';
import { Control, FieldErrors, FieldValues, UseFormRegister, useFieldArray } from 'react-hook-form';
import { BsPlus, BsTrash } from 'react-icons/bs';
import { ZodObject, ZodRawShape } from 'zod';
import { ApiError } from '../../../api';
import { ShowFieldErrors } from '../../../utils/forms';
import { useSafeParseFormField } from '../../hooks/safe-parse-form-field';
import { FileUploader } from '../FileUploader';
import { FormCheckboxField } from '../FormCheckboxField';
import { FormFeedback } from '../FormFeedback';
import { FormFieldVisibility, FormLabel } from '../FormLabel';
import { FormTextField } from '../FormTextField';
import { SelectController } from '../SelectController';
import { FORM_NAME, MAX_SIZE, ACCEPT } from './constants';

interface ImmunizationFormGroupProps {
  control: Control;
  errors: FieldErrors;
  serverErrors: ApiError | undefined;
  register: UseFormRegister<FieldValues>;
  showServerFieldErrors: ShowFieldErrors | undefined;
  setShowServerFieldErrors: (showServerFieldErrors: ShowFieldErrors) => void;
  validationSchema: ZodObject<ZodRawShape>;
}

export const ImmunizationFormGroup = ({
  control,
  errors,
  serverErrors,
  showServerFieldErrors,
  setShowServerFieldErrors,
  register,
  validationSchema,
}: ImmunizationFormGroupProps) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: FORM_NAME, // unique name for your Field Array
  });
  const isParsed = useSafeParseFormField(control, FORM_NAME, validationSchema);

  const onAppendClick = () => {
    append({
      file__file: null,
      type: null,
      exempt: false,
      issue_date: null,
      expiration_date: null,
    });
  };

  const onRemoveClick = (index: number) => {
    remove(index);
  };

  return (
    <Form.Group className="mb-3">
      <FormLabel visibility={FormFieldVisibility.CONNECTED}>Immunization Certificates</FormLabel>
      <ul className="list-unstyled">
        {fields.map((item, index) => (
          <li key={item.id} className="mt-4 border rounded p-3">
            <Form.Group>
              <Row>
                <Col xs={12}>
                  <Row className="justify-content-end align-items-center mb-4">
                    <Col>
                      <span className="text-primary fw-bold">Immunization #{index + 1}</span>
                    </Col>
                    <Col xs="auto">
                      <Button
                        type="button"
                        variant="link"
                        className="text-danger"
                        size="lg"
                        onClick={() => onRemoveClick(index)}
                      >
                        <BsTrash />
                      </Button>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} className="mb-4">
                      <Form.Group controlId={`${FORM_NAME}.${index}.file__file`}>
                        <FileUploader
                          accept={ACCEPT}
                          maxSize={MAX_SIZE}
                          name={`${FORM_NAME}.${index}.file__file`}
                          disabledPreview={true}
                          control={control}
                          className="mb-3"
                          dest="providers_documents"
                          isClearable={false}
                          isInvalid={
                            !!errors[FORM_NAME]?.[index]?.file ||
                            !!showServerFieldErrors?.[index]?.['file__file']
                          }
                        />
                        <FormFeedback
                          clientError={errors.immunizations?.[index]?.['file__file']}
                          serverErrors={serverErrors?.body.immunizations?.[index]?.file}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} className="mb-3">
                      <FormLabel visibility={FormFieldVisibility.CONNECTED}>
                        Type<span className="text-danger"> *</span>
                      </FormLabel>
                      <SelectController
                        name={`${FORM_NAME}.${index}.type`}
                        control={control}
                        options={Object.entries(window.IMMUNIZATION_TYPES).map(
                          ([value, label]) => ({
                            value,
                            label,
                          })
                        )}
                        isClearable={false}
                        isMulti={false}
                        placeholder=""
                        onChange={() => {
                          setShowServerFieldErrors((prev) => {
                            const newErrors = { ...prev };
                            if (newErrors[index]?.type) {
                              delete newErrors[index].type;
                            }
                            return newErrors;
                          });
                        }}
                      />
                      <FormFeedback
                        clientError={errors.immunizations?.[index]?.type}
                        serverErrors={serverErrors?.body.immunizations?.[index]?.type}
                      />
                    </Col>
                    <Col xs={12} className="mb-3">
                      <FormCheckboxField
                        name={`${FORM_NAME}.${index}.exempt`}
                        label="Exempt (Religious, Medical, or other) - please upload documentation."
                        serverErrors={serverErrors}
                        showServerFieldErrors={showServerFieldErrors}
                        setShowServerFieldErrors={setShowServerFieldErrors}
                        errors={errors}
                        register={register}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <FormTextField
                        type="date"
                        name={`${FORM_NAME}.${index}.issue_date`}
                        label="Date Given"
                        serverErrors={serverErrors}
                        showServerFieldErrors={showServerFieldErrors}
                        setShowServerFieldErrors={setShowServerFieldErrors}
                        errors={errors}
                        register={register}
                      />
                    </Col>
                    <Col xs={12} sm={6}>
                      <FormTextField
                        type="date"
                        name={`${FORM_NAME}.${index}.expiration_date`}
                        label="Expiration Date"
                        serverErrors={serverErrors}
                        showServerFieldErrors={showServerFieldErrors}
                        setShowServerFieldErrors={setShowServerFieldErrors}
                        errors={errors}
                        register={register}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form.Group>
          </li>
        ))}
      </ul>
      <Button
        className="d-flex align-items-center"
        variant="outline-primary"
        type="button"
        disabled={!!fields.length && !isParsed}
        onClick={onAppendClick}
      >
        <BsPlus className="me-1" /> Add Immunization
      </Button>
    </Form.Group>
  );
};
